// Column mapping for spreadsheet imports: find the table in a workbook, guess which column feeds each field, apply a
// mapping. Pure functions, used by the ColumnMapper pop-up and tested against Cesar's own files.
import type { Cell, Sheet } from "./xlsx";

export interface FieldSpec {
  key: string;
  label: string;
  required?: boolean;
  /** Header names this field is commonly given. Matched after normalising (case, spaces, punctuation). */
  aliases: string[];
  hint?: string;
}

export interface TableGuess {
  sheet: number;
  headerRow: number; // 0-based
  score: number;
}

export interface MappedRow {
  line: number; // 1-based sheet row
  values: Record<string, Cell>;
}

export const normHeader = (v: Cell) =>
  String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9%$#]+/g, " ")
    .trim();

// Words with a crude plural trim, so "chains" matches "chain" and "codes" matches "code".
const words = (s: string) => new Set(s.split(" ").filter((w) => w.length > 1 || /[#%$]/.test(w)).map((w) => (w.length > 3 && w.endsWith("s") ? w.slice(0, -1) : w)));

/** How well a header cell matches a field: 1 = exact alias, 0.5-0.9 = word overlap, 0 = none. */
export function headerScore(header: Cell, field: FieldSpec): number {
  const h = normHeader(header);
  if (!h) return 0;
  let best = 0;
  for (const a of [field.label, ...field.aliases]) {
    const n = normHeader(a);
    if (!n) continue;
    if (h === n || [...words(h)].join(" ") === [...words(n)].join(" ")) return 1;
    const hw = words(h);
    const aw = words(n);
    if (!aw.size || !hw.size) continue;
    let common = 0;
    for (const w of aw) if (hw.has(w)) common++;
    // All alias words present in the header (e.g. alias "chain" in "doc dlv milestones chains" after plural trim).
    const cover = common / aw.size;
    const precision = common / hw.size;
    const s = cover === 1 ? 0.6 + 0.3 * precision : cover >= 0.5 ? 0.4 * cover + 0.2 * precision : 0;
    if (s > best) best = s;
  }
  return best;
}

/** Assign columns to fields greedily by score, one column per field and one field per column. */
export function guessMapping(header: Cell[], fields: FieldSpec[], remembered?: Record<string, string>): Record<string, number | null> {
  const pairs: { f: string; c: number; s: number }[] = [];
  header.forEach((h, c) => {
    for (const f of fields) {
      let s = headerScore(h, f);
      if (remembered?.[f.key] && remembered[f.key] === normHeader(h)) s = 2; // the user's last choice for this header wins
      if (s >= 0.5) pairs.push({ f: f.key, c, s });
    }
  });
  pairs.sort((a, b) => b.s - a.s);
  const out: Record<string, number | null> = Object.fromEntries(fields.map((f) => [f.key, null]));
  const usedCols = new Set<number>();
  for (const p of pairs) {
    if (out[p.f] !== null || usedCols.has(p.c)) continue;
    out[p.f] = p.c;
    usedCols.add(p.c);
  }
  return out;
}

/** Find the sheet and header row that best match the fields (scans the first 30 rows of every sheet). */
export function detectTable(sheets: Sheet[], fields: FieldSpec[]): TableGuess {
  let best: TableGuess = { sheet: 0, headerRow: 0, score: -1 };
  sheets.forEach((sh, si) => {
    for (let r = 0; r < Math.min(30, sh.rows.length); r++) {
      const row = sh.rows[r] ?? [];
      const m = guessMapping(row, fields);
      let score = 0;
      for (const f of fields) {
        const c = m[f.key];
        if (c !== null) score += headerScore(row[c], f) * (f.required ? 2 : 1);
      }
      // Prefer tables with data under them.
      const dataRows = sh.rows.slice(r + 1, r + 50).filter((x) => x && x.some((v) => v !== null && v !== "")).length;
      score += Math.min(dataRows, 20) / 100;
      if (score > best.score) best = { sheet: si, headerRow: r, score };
    }
  });
  return best;
}

/** Rows under the header, mapped to field keys. Fully blank rows are skipped. */
export function applyMapping(rows: Cell[][], headerRow: number, mapping: Record<string, number | null>): MappedRow[] {
  const out: MappedRow[] = [];
  for (let r = headerRow + 1; r < rows.length; r++) {
    const row = rows[r] ?? [];
    const values: Record<string, Cell> = {};
    let any = false;
    for (const [k, c] of Object.entries(mapping)) {
      if (c === null) continue;
      const v = row[c] ?? null;
      const clean = typeof v === "string" ? v.trim() : v;
      values[k] = clean === "" ? null : clean;
      if (values[k] !== null) any = true;
    }
    if (any) out.push({ line: r + 1, values });
  }
  return out;
}

/** Column letter for display (0 -> A, 26 -> AA). */
export function colLetter(i: number): string {
  let s = "";
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

export const str = (v: Cell) => (v === null || v === undefined ? "" : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).trim());
export const num = (v: Cell): number | null => {
  if (v === null || v === "" || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return isFinite(n) ? n : null;
};
